<template>
    <div class="mb-6 lg:mb-0">
        <!-- Title with responsive sizing -->
        <div class="mb-6">
            <Heading :level="2" :title="organization?.name || ''" class="truncate" />
        </div>

        <!-- Navigation menu - consistent across all devices -->
        <div>
            <div class="mb-2 text-xs font-semibold uppercase text-slate-400">Menu</div>
            <ul>
                <!-- Users link -->
                <router-link v-slot="{ href, navigate, isExactActive }" :to="{ name: 'organization-users' }" custom>
                    <li>
                        <a
                            class="block w-full rounded px-3 py-2.5"
                            :class="isExactActive ? 'bg-white shadow-sm' : ''"
                            :href="href"
                            @click="navigate"
                        >
                            <div class="flex items-center">
                                <AppIcon
                                    name="users"
                                    class="mr-3 h-4 w-4"
                                    :class="isExactActive ? 'text-indigo-500' : 'text-slate-400'"
                                />
                                <span
                                    class="text-sm font-medium"
                                    :class="isExactActive ? 'text-indigo-500' : 'text-slate-600'"
                                >
                                    Users
                                </span>
                            </div>
                        </a>
                    </li>
                </router-link>

                <!-- Permissions link (staff only) -->
                <router-link
                    v-if="isSfStaff"
                    v-slot="{ href, navigate, isExactActive }"
                    :to="{ name: 'organization-permissions' }"
                    custom
                >
                    <li>
                        <a
                            class="block w-full rounded px-3 py-2.5"
                            :class="isExactActive ? 'bg-white shadow-sm' : ''"
                            :href="href"
                            @click="navigate"
                        >
                            <div class="flex items-center">
                                <AppIcon
                                    name="unlock"
                                    class="mr-3 h-4 w-4"
                                    :class="isExactActive ? 'text-indigo-500' : 'text-slate-400'"
                                />
                                <span
                                    class="text-sm font-medium"
                                    :class="isExactActive ? 'text-indigo-500' : 'text-slate-600'"
                                >
                                    Permissions
                                </span>
                            </div>
                        </a>
                    </li>
                </router-link>

                <!-- Usage link (admin only) -->
                <router-link
                    v-if="
                        (isSfStaff ||
                            ['admin', 'superadmin'].includes(selfActiveOrganization?.organization_operator?.role)) &&
                        !selfActiveOrganization.is_legacy_billing === true
                    "
                    v-slot="{ href, navigate, isExactActive }"
                    :to="{ name: 'organization-usage' }"
                    custom
                >
                    <li>
                        <a
                            class="block w-full rounded px-3 py-2.5"
                            :class="isExactActive ? 'bg-white shadow-sm' : ''"
                            :href="href"
                            @click="navigate"
                        >
                            <div class="flex items-center">
                                <AppIcon
                                    name="gauge"
                                    class="mr-3 h-4 w-4"
                                    :class="isExactActive ? 'text-indigo-500' : 'text-slate-400'"
                                />
                                <span
                                    class="text-sm font-medium"
                                    :class="isExactActive ? 'text-indigo-500' : 'text-slate-600'"
                                >
                                    Usage
                                </span>
                            </div>
                        </a>
                    </li>
                </router-link>

                <!-- Subscriptions link (admin only) -->
                <router-link
                    v-if="
                        // (
                        //     isSfStaff ||
                        //     ['admin', 'superadmin'].includes(
                        //         selfActiveOrganization?.organization_operator?.role,
                        //     )
                        // )
                        !selfActiveOrganization.is_legacy_billing === true
                    "
                    v-slot="{ href, navigate, isExactActive }"
                    :to="{ name: 'organization-subscriptions' }"
                    custom
                >
                    <li>
                        <a
                            class="block w-full rounded px-3 py-2.5"
                            :class="isExactActive ? 'bg-white shadow-sm' : ''"
                            :href="href"
                            @click="navigate"
                        >
                            <div class="flex items-center">
                                <AppIcon
                                    name="cart-shopping"
                                    class="mr-3 h-4 w-4"
                                    :class="isExactActive ? 'text-indigo-500' : 'text-slate-400'"
                                />
                                <span
                                    class="text-sm font-medium"
                                    :class="isExactActive ? 'text-indigo-500' : 'text-slate-600'"
                                >
                                    Subscriptions
                                </span>
                            </div>
                        </a>
                    </li>
                </router-link>

                <!-- Unlocked creators -->
                <router-link
                    v-if="selfActiveOrganization.is_legacy_billing !== true"
                    v-slot="{ href, navigate, isExactActive }"
                    :to="{ name: 'organization-unlocked-creators' }"
                    custom
                >
                    <li>
                        <a
                            class="block w-full rounded px-3 py-2.5"
                            :class="isExactActive ? 'bg-white shadow-sm' : ''"
                            :href="href"
                            @click="navigate"
                        >
                            <div class="flex items-center">
                                <AppIcon
                                    name="user-unlock"
                                    class="mr-3 h-4 w-4"
                                    :class="isExactActive ? 'text-indigo-500' : 'text-slate-400'"
                                />
                                <span
                                    class="text-sm font-medium"
                                    :class="isExactActive ? 'text-indigo-500' : 'text-slate-600'"
                                >
                                    Unlocked Creators
                                </span>
                            </div>
                        </a>
                    </li>
                </router-link>

                <!-- Email accounts -->
                <router-link
                    v-if="checkPermission('feature.messages.integration')"
                    v-slot="{ href, navigate, isExactActive }"
                    :to="{ name: 'organization-email-accounts' }"
                    custom
                >
                    <li>
                        <a
                            class="block w-full rounded px-3 py-2.5"
                            :class="isExactActive ? 'bg-white shadow-sm' : ''"
                            :href="href"
                            @click="navigate"
                        >
                            <div class="flex items-center">
                                <AppIcon
                                    name="envelope"
                                    class="mr-3 h-4 w-4"
                                    :class="isExactActive ? 'text-indigo-500' : 'text-slate-400'"
                                />
                                <span
                                    class="text-sm font-medium"
                                    :class="isExactActive ? 'text-indigo-500' : 'text-slate-600'"
                                >
                                    Email Accounts
                                </span>
                            </div>
                        </a>
                    </li>
                </router-link>

                <!-- Settings link -->
                <router-link v-slot="{ href, navigate, isExactActive }" :to="{ name: 'organization-settings' }" custom>
                    <li>
                        <a
                            class="block w-full rounded px-3 py-2.5"
                            :class="isExactActive ? 'bg-white shadow-sm' : ''"
                            :href="href"
                            @click="navigate"
                        >
                            <div class="flex items-center">
                                <AppIcon
                                    name="gear"
                                    class="mr-3 h-4 w-4"
                                    :class="isExactActive ? 'text-indigo-500' : 'text-slate-400'"
                                />
                                <span
                                    class="text-sm font-medium"
                                    :class="isExactActive ? 'text-indigo-500' : 'text-slate-600'"
                                >
                                    Settings
                                </span>
                            </div>
                        </a>
                    </li>
                </router-link>
            </ul>
        </div>
    </div>
</template>

<script>
import { mapGetters } from 'vuex'
import { Heading } from '@/components/ui/typography'

export default {
    name: 'OrganizationSidebar',

    components: {
        Heading,
    },

    props: {
        organization: {
            type: Object,
        },
    },

    computed: {
        ...mapGetters(['getFromDictionary', 'isSfStaff', 'checkPermission', 'selfActiveOrganization']),
    },
}
</script>

<style scoped>
/* Hide scrollbar but keep functionality */
.no-scrollbar {
    -ms-overflow-style: none; /* IE and Edge */
    scrollbar-width: none; /* Firefox */
}
.no-scrollbar::-webkit-scrollbar {
    display: none; /* Chrome, Safari, Opera */
}
</style>
